import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {Icon} from "../../AvailableJobsPage.styles";
import {Shift, ShiftHeader, ShiftsContainer, SmallBox, BoostBadge} from "./Shifts.styles";

import heartFilled from 'images/heartFilled.svg'
import heartUnfilled from 'images/heartUnfilled.svg'
import instabook from 'images/job-option/instabook.png'
import friendlyEndorsed from 'images/job-option/friendlyEndorsed.png'
import equipmentEndorsed from 'images/job-option/equipmentEndorsed.png'
import spotlight from 'images/icons/spotlight.png'
import {formatDate, getDaysFromDate} from "../../../../../utils";
import { HygienistProps, JobProps, MembershipStatusResponse, PartialOffer, PositionProps } from "utils/types";
import { calculateRateRange } from "../../../../../utils/rateCalculations";
import { RateBenchmarkResponse } from "../../../../../api/Api";
import {
    buildRateBenchmarkRequestParamsFromJob,
    getTalentMarketBenchmarkRate,
    RateBenchmarkRequestParams,
} from "../../../../../utils/rateGuidance";
import { canRequestMaxBookedIncrease, canUseInstabookByMembership, getMembershipRestrictionDialogCopy, getRateCalculationTalent } from "../../../../../utils/membership";
import {usePersistedState} from "../../../../../hooks";
import entities from "../../../../../reduxStore/entityKeys";
import Popup from "../../../../../components/Popup";
import useActionApi from "../../../../../hooks/useActionApi";
import Api from "../../../../../api";
import OfferModal from "../../../AvailableJobPage/components/OfferModal";
import ConfirmDialog from "../../../../../components/ConfirmDialog";
import { MembershipRestrictionModal, type MembershipDialogCopy } from "../../../../../components/Modal";
import {PopupContainer, PriorityShiftContainer} from "../../../../../components/EditJob/common.styles";
import styles from "../../../../dentist/PostJobPage/PostJob.module.scss";
import {CrossButtonBlack} from "../../../../../components/Buttons/CrossButton";
import clsx from "clsx";
import DefaultButton from "../../../../../components/Buttons/DefaultButton";
import {useHistory} from "react-router";
import FixedLoader from "../../../../../components/FixedLoader";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CancellationLabel from '../../../../../components/CancellationLabel';
import { Analytics, getPlatformAndVersion, generateInsertId } from "../../../../../utils/analytics";
import MaxBookedPopup from "../../../AvailableJobPage/components/MaxBookedPopup";
import { JOB_STATUSES, PARTIAL_OFFER_STATUS } from "utils/constants";
import { Tooltip, Chip, ClickAwayListener } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface ShiftsProps {
    availableJobs: JobProps[],
    goToPage: (id: number, filter: any, isFavourite: boolean) => void,
    membershipStatus?: MembershipStatusResponse | null,
    errorGetJos: string,
    updating: boolean,
    recentlyViewedShiftId?: number | null,
    emptyMessage?: string,
}


/**
 * Anchors the card around the office posted rate and keeps the
 * personalized range as secondary helper context.
 */
interface RateBenchmarkRequestEntry {
    key: string;
    params: RateBenchmarkRequestParams;
}

const getRateBenchmarkRequestKey = (params: RateBenchmarkRequestParams): string => {
    const queryParams = new URLSearchParams({
        jobType: String(params.jobType),
    });

    if (params.locationId !== undefined && params.shiftDate && params.shiftStartTime) {
        queryParams.set('locationId', String(params.locationId));
        queryParams.set('shiftDate', params.shiftDate);
        queryParams.set('shiftStartTime', params.shiftStartTime);
    } else if (params.province && params.postalPrefix) {
        queryParams.set('province', params.province);
        queryParams.set('postalPrefix', params.postalPrefix);
    }

    return queryParams.toString();
};

const getDisplayRateRange = (
    rateTalent: Pick<HygienistProps, 'graduationYear' | 'status'> | undefined,
    rate: number,
    rateBenchmark?: RateBenchmarkResponse | null
): JSX.Element => {
    const marketBenchmarkTopFloorRate = getTalentMarketBenchmarkRate(rateBenchmark);
    const range = calculateRateRange(
        rate,
        rateTalent?.graduationYear,
        rateTalent?.status,
        undefined,
        marketBenchmarkTopFloorRate
    );

    if (range.bottomRate === 0 || range.topRate === 0) {
        return <span>$0</span>;
    }

    const deEmph: React.CSSProperties = {
        fontSize: '12px',
        color: '#9AA4B2',
        fontWeight: 400,
    };

    return (
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 10px', lineHeight: 1.2 }}>
            <span style={{ fontSize: '18px', fontWeight: 700, color: '#05132E' }}>${range.proposedRate}/hr</span>
            <span style={deEmph}>Your range: ${range.bottomRate} - ${range.topRate}/hr</span>
        </span>
    );
}

const DataBox = ({title, data}: {title: string, data: string | any}): JSX.Element => {
    return <SmallBox className={'col'}><span className={'title'}>{title}</span>{data}</SmallBox>
}

const formatAvgApptTime = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'number' && Number.isFinite(value)) return `${value} min`;
    const num = parseInt(String(value), 10);
    return Number.isFinite(num) ? `${num} min` : String(value);
}

const distance = (job: JobProps, country?: 0 | 1) => {
    const isUS = country === 1;
    // Prefer numeric value if present, fallback to job.distance (km as string)
    const meters = typeof job?.distanceObj?.distance?.value === 'number' ? job.distanceObj.distance.value : undefined;
    const kmFromString = job?.distance ? parseFloat(job.distance) : undefined;

    let distanceText = '';
    if (typeof meters === 'number') {
        const km = meters / 1000;
        if (isUS) {
            const miles = km * 0.621371;
            distanceText = `${Math.round(miles)}mi`;
        } else {
            distanceText = `${Math.round(km)}km`;
        }
    } else if (typeof kmFromString === 'number' && !isNaN(kmFromString)) {
        if (isUS) {
            const miles = kmFromString * 0.621371;
            distanceText = `${Math.round(miles)}mi`;
        } else {
            distanceText = `${Math.round(kmFromString)}km`;
        }
    } else {
        distanceText = isUS ? '0mi' : '0km';
    }

    // Duration: try numeric seconds first, then text, otherwise omit
    let approxText: string | null = null;
    const durationVal = job?.distanceObj?.duration?.value;
    if (typeof durationVal === 'number') {
        const mins = Math.max(0, Math.round(durationVal / 60));
        const unit = mins === 1 ? 'min' : 'mins';
        approxText = `${mins} ${unit}`;
    } else if (job?.distanceObj?.duration?.text) {
        approxText = job.distanceObj.duration.text
            .replace('about ', '')
            .replace('approximately ', '')
            .replace('minute', 'min')
            .replace('minsute', 'min');
    }

    const approxLine = approxText ? `approx. ${approxText}` : 'approx.';

    return <>{distanceText}<br/><span className={'approxText'}>({approxLine})</span></>
}

interface EnhancedProfileModalProps {
    resumeExpDate: Date | undefined;
    onLater: () => void;
    onClose: () => void;
    onDoItNow: () => void;
}

const EnhancedProfileModal: React.FC<EnhancedProfileModalProps> = ({ resumeExpDate,  onLater, onClose, onDoItNow }) => {
    const isCanBePostponed = resumeExpDate ? (resumeExpDate > new Date()) : true;
    return (
        <PopupContainer>
            <div className={styles.titleContainer}>
                <div className={styles.popupTitle}>Enhanced Profile Required</div>
                <CrossButtonBlack className={clsx("no-fastclick", styles.crossButtonDark)} onClick={onClose}/>
            </div>
            <div >
                <p>We are moving away from resumes to a more enhanced user profile. Please complete your enhanced profile.</p>
            </div>
            <div>
                <DefaultButton variant="contained" color="primary" onClick={onDoItNow}>Do it now</DefaultButton>
                {isCanBePostponed ? <DefaultButton variant="contained" onClick={onLater}>Later</DefaultButton> : null}
            </div>
        </PopupContainer>
    );
};

interface WorkExperienceModalProps {
    onClose: () => void;
}

/**
 * DateString is a to-string-type-converted Date.
 */
type DateString = string;
function formatDateString(d: DateString | undefined): string {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

const WorkExperienceModal: React.FC<WorkExperienceModalProps> = ({ onClose }) => {
    const history = useHistory();
    const hygienist = usePersistedState<HygienistProps>({
        entityName: entities.hygienist
    })[0];

    return (
        <PopupContainer style={{ width: '100%', maxWidth: 460, margin: '0 auto' }}>
            <div className={styles.titleContainer}>
                <div className={styles.popupTitle}>{`Heads up, ${hygienist?.firstName}`}</div>
                <CrossButtonBlack className={clsx("no-fastclick", styles.crossButtonDark)} onClick={onClose}/>
            </div>
            <div>
                <p>
                    The response to the enhanced profiles has been great, although we have received consistent feedback that dental offices need to see Work Experience in temping shift offers, so we've made this a required section of the profiles. <br/><br/>
                    It looks like you don't have yours filled out just yet - so head on over there to fill it out.
                </p>
            </div>
            <div>
                <DefaultButton 
                    variant="contained" 
                    color="primary" 
                    className={styles.modalButton}
                    onClick={() => history.push('hygienistProfile', { isOpenPositionModal: true })}
                >
                    Fill Out Work Experience
                </DefaultButton>
                <DefaultButton 
                    variant="contained"
                    className={styles.modalButton}
                    onClick={onClose}
                >
                    I'll do this later
                </DefaultButton>
            </div>
        </PopupContainer>
    );
};

const BonusRateTooltip = ({ job }: { job: JobProps }) => {
    const [open, setOpen] = useState(false);

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <div style={{ display: 'inline-block' }}>
                <Tooltip 
                    title={
                        <div style={{ padding: '4px' }}>
                            <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                                💰 Bonus: +${job.ghrBonus?.rate?.toFixed(2)}/hr
                            </div>
                            <div style={{ fontSize: '1em', opacity: 0.9 }}>
                                {
                                    `Earn $${
                                        ((job.ghrBonus?.windowMeta?.tier1RateCents ?? 0) / 100)
                                    }/hr extra paid by TempStars, for your first five shifts if completed by ${
                                        formatDateString(job.ghrBonus?.endDate)
                                    }. Then earn $${
                                        ((job.ghrBonus?.windowMeta?.tier2RateCents ?? 0) / 100)
                                    }/hr extra on all shifts you book that get completed ${
                                        job.ghrBonus?.tier2EndDate
                                        ? `before the end of ${formatDateString(job.ghrBonus?.tier2EndDate)}`
                                        : `in the next ${job.ghrBonus?.windowMeta?.tier2DurationDays} days`
                                    }`
                                }
                            </div>
                        </div>
                    }
                    open={open}
                    onClose={() => setOpen(false)}
                    onOpen={() => setOpen(true)}
                    arrow
                    placement="top"
                >
                    <Chip 
                        label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                Bonus Rate Available
                                <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                            </span>
                        }
                        size="small"
                        color={job.ghrBonus?.tier === 2 ? 'success' : 'primary'}
                        sx={{ 
                            height: 22, 
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: 'pointer'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpen(prev => !prev);
                        }}
                    />
                </Tooltip>
            </div>
        </ClickAwayListener>
    );
};

const Shifts = ({availableJobs, goToPage, membershipStatus, errorGetJos, updating, recentlyViewedShiftId, emptyMessage}: ShiftsProps): JSX.Element => {
    const history = useHistory();
    const hygienist = usePersistedState<HygienistProps>({
        entityName: entities.hygienist
    })[0];
    const [isEnhancedProfileModalOpen, setEnhancedProfileModalOpen] = useState<boolean>(false);
    const [isWorkExperienceModalOpen, setWorkExperienceModalOpen] = useState<boolean>(false);
    const [workExperience, setWorkExperience] = useState<PositionProps[]>([]);
    const [resumeExpDate, setResumeExpDate] = useState<Date | undefined>();
    const [jobId, setJobId] = useState<number>();
    const [filters, setFilters] = useState<{ date: string }>();
    const [favouriteHygienistLocations, setFavouriteHygienistLocations] = useState<number[]>([]);
    const [listRateBenchmarks, setListRateBenchmarks] = useState<Record<string, RateBenchmarkResponse | null>>({});
    const [quickApplyOpenFor, setQuickApplyOpenFor] = useState<number | null>(null);
    const [quickApplyOfferStats, setQuickApplyOfferStats] = useState<{ offerCount: number; avgRate: number | null } | null>(null);
    const [quickApplyRateBenchmark, setQuickApplyRateBenchmark] = useState<RateBenchmarkResponse | null>(null);
    const quickApplyJobIdRef = useRef<number | null>(null);
    const inFlightListRateBenchmarkKeysRef = useRef<Set<string>>(new Set());
    const [showJobSeekingStatus, setShowJobSeekingStatus] = useState<boolean>(true);
    const lastQuickApplyClickTsRef = useRef<number>(0);
    const [isMaxBookedOffers, setIsMaxBookedOffers] = useState<boolean>(false);
    const [isMaxConfirmedJobs, setIsMaxConfirmedJobs] = useState<boolean>(false);
    const [isMaxBookedPopupOpen, setIsMaxBookedPopupOpen] = useState<boolean>(false);
    const [completedShifts, setCompletedShifts] = useState<number>(0);
    const [highlightedShiftId, setHighlightedShiftId] = useState<number | null>(null);
    const [membershipDialog, setMembershipDialog] = useState<MembershipDialogCopy | null>(null);

    const platformAndVersion = useMemo(() => getPlatformAndVersion(), []);
    const rateTalent = useMemo(
        () => getRateCalculationTalent(hygienist, membershipStatus),
        [hygienist, membershipStatus]
    );

    const [quickApplyLoading, , makeQuickApplyOffer] = useActionApi({
        apiMethod: Api.makePartialOffer,
        onSuccess: () => {
            handleCloseQuickApply();
            history.push({ pathname: 'hygienistHome' });
        },
        onError: (err) => {
            const membershipRestrictionDialog = getMembershipRestrictionDialogCopy(err);
            if (membershipRestrictionDialog) {
                quickApplyJobIdRef.current = null;
                setQuickApplyOpenFor(null);
                setQuickApplyOfferStats(null);
                setQuickApplyRateBenchmark(null);
                setMembershipDialog(membershipRestrictionDialog);
            }
        },
    });

    const [getResumesExpirationDateLoading, , getResumesExpirationDate] = useActionApi({
        apiMethod: Api.getResumesExpirationDate,
        onSuccess: (resumeExpDate: string) => {
            setResumeExpDate(new Date(resumeExpDate))
        },
    });

    const [getHygienistWorkExperienceLoading, , getHygienistWorkExperience] = useActionApi({
        apiMethod: Api.getHygienistWorkExperience,
        onSuccess: (res: PositionProps[]) => {
            setWorkExperience(res)
        },
    });

    const [checkFavouriteHygienistLocationsLoading, , checkFavouriteHygienistLocations] = useActionApi({
        apiMethod: Api.checkFavouriteHygienistLocations,
        onSuccess: (res: { isFavouriteHygienist: number[] }) => {
            setFavouriteHygienistLocations(res.isFavouriteHygienist)
        },
    });

    const [getHygienistOffersLoading, , getHygienistOffers] = useActionApi({
        apiMethod: Api.getHygienistOffers,
        onSuccess: (offers: PartialOffer[]) => {
            const postedOffers = offers.filter(offer => offer.status === PARTIAL_OFFER_STATUS.POSTED)
            setIsMaxBookedOffers(postedOffers.length >= hygienist?.maxBooked * 2);
        },
        onError: (error) => {
                console.error('Error fetching hygienist offers:', error);
        }
    });

    const [getHygienistJobsLoading, , getHygienistJobs] = useActionApi({
        apiMethod: Api.getHygienistJobs,
        onSuccess: (jobs: JobProps[]) => {
            const confirmedJobs = jobs.filter(job => job.status === JOB_STATUSES.CONFIRMED)
            setIsMaxConfirmedJobs(confirmedJobs.length >= hygienist?.maxBooked);
        },
        onError: (error) => {
            if (process.env.NODE_ENV in ['local', 'staging']) {
                console.error('Error fetching hygienist jobs:', error);
            }
        }
    });

    const onUpdateProfileLater = () => {
        setEnhancedProfileModalOpen(false);
        if (jobId && filters) {
            const job = availableJobs.find(job => job.id === jobId);
            return goToPage(jobId, filters, job ? isFavouriteHygienist(job) : false);
        }
    }

    const openModal = async (jobId: number, filters: any) => {
        setJobId(jobId);
        setFilters(filters);

        if (hygienist?.skills?.length === 0) {
            return setEnhancedProfileModalOpen(true);
        } else if(!workExperience || workExperience?.length === 0){
            return setWorkExperienceModalOpen(true);
        } else {
            const job = availableJobs.find(job => job.id === jobId);
            return goToPage(jobId, filters, job ? isFavouriteHygienist(job) : false);
        }
    }

    const isFavouriteHygienist = (job: JobProps) => {
        return favouriteHygienistLocations.includes(job.locationId);
    }

    useEffect(() => {
        getResumesExpirationDate({})
        getHygienistWorkExperience(hygienist.id)
        setCompletedShifts(hygienist?.completedJobs);
        getHygienistJobs('');
        getHygienistOffers({});
    }, [hygienist]);

    useEffect(() => {
        checkFavouriteHygienistLocations(availableJobs.map(job => job.locationId))
    }, [availableJobs])

    const { rateBenchmarkRequestEntries, rateBenchmarkKeyByJobId } = useMemo(() => {
        const entriesByKey = new Map<string, RateBenchmarkRequestEntry>();
        const keyByJobId: Record<number, string> = {};

        availableJobs.forEach((job) => {
            const params = buildRateBenchmarkRequestParamsFromJob(job);
            if (!params) {
                return;
            }

            const key = getRateBenchmarkRequestKey(params);
            keyByJobId[job.id] = key;

            if (!entriesByKey.has(key)) {
                entriesByKey.set(key, { key, params });
            }
        });

        return {
            rateBenchmarkRequestEntries: Array.from(entriesByKey.values()),
            rateBenchmarkKeyByJobId: keyByJobId,
        };
    }, [availableJobs]);

    useEffect(() => {
        let isCancelled = false;
        const requestsToFetch = rateBenchmarkRequestEntries.filter(({ key }) =>
            listRateBenchmarks[key] === undefined && !inFlightListRateBenchmarkKeysRef.current.has(key)
        );

        if (!requestsToFetch.length) {
            return;
        }

        requestsToFetch.forEach(({ key }) => inFlightListRateBenchmarkKeysRef.current.add(key));

        Promise.allSettled(
            requestsToFetch.map(async ({ key, params }) => ({
                key,
                benchmark: await Api.getRateBenchmarks(params),
            }))
        )
            .then((results) => {
                if (isCancelled) {
                    return;
                }

                setListRateBenchmarks((prev) => {
                    const next = { ...prev };

                    results.forEach((result, index) => {
                        const { key } = requestsToFetch[index];
                        next[key] = result.status === 'fulfilled' ? result.value.benchmark : null;
                    });

                    return next;
                });
            })
            .finally(() => {
                requestsToFetch.forEach(({ key }) => inFlightListRateBenchmarkKeysRef.current.delete(key));
            });

        return () => {
            isCancelled = true;
        };
    }, [listRateBenchmarks, rateBenchmarkRequestEntries]);

    // Handle recently viewed shift highlighting
    useEffect(() => {
        if (!recentlyViewedShiftId) {
            setHighlightedShiftId(null);
            return;
        }
        setHighlightedShiftId(recentlyViewedShiftId);
        const timeoutId = window.setTimeout(() => setHighlightedShiftId(null), 2000);
        return () => window.clearTimeout(timeoutId);
    }, [recentlyViewedShiftId]);

    const handleOpenQuickApply = useCallback((jobId: number) => {
        if (isMaxBookedOffers || isMaxConfirmedJobs) {
            setIsMaxBookedPopupOpen(true);
            return;
        }

        const quickApplyJob = availableJobs.find(job => job.id === jobId);
        if (!quickApplyJob) {
            return;
        }

        quickApplyJobIdRef.current = jobId;
        setQuickApplyOfferStats(null);
        setQuickApplyRateBenchmark(null);
        setQuickApplyOpenFor(jobId);

        Api.getJobDetails(jobId)
            .then((jobData: JobProps) => {
                if (quickApplyJobIdRef.current !== jobId) return;
                setQuickApplyOfferStats(jobData?.offerStats ?? { offerCount: 0, avgRate: null });
            })
            .catch(() => {});

        const benchmarkParams = buildRateBenchmarkRequestParamsFromJob(quickApplyJob);
        if (!benchmarkParams) {
            return;
        }

        const benchmarkKey = getRateBenchmarkRequestKey(benchmarkParams);
        if (listRateBenchmarks[benchmarkKey] !== undefined) {
            setQuickApplyRateBenchmark(listRateBenchmarks[benchmarkKey]);
            return;
        }

        Api.getRateBenchmarks(benchmarkParams)
            .then((benchmark: RateBenchmarkResponse) => {
                if (quickApplyJobIdRef.current !== jobId) return;
                setQuickApplyRateBenchmark(benchmark);
            })
            .catch(() => {});
    }, [availableJobs, isMaxBookedOffers, isMaxConfirmedJobs, listRateBenchmarks]);
    
    const handleCloseQuickApply = useCallback(() => {
        quickApplyJobIdRef.current = null;
        setQuickApplyOpenFor(null);
        setQuickApplyOfferStats(null);
        setQuickApplyRateBenchmark(null);
    }, []);

    return (
        <ShiftsContainer>
            <Popup opened={isEnhancedProfileModalOpen} onClose={() => setEnhancedProfileModalOpen(false)}>
                <EnhancedProfileModal
                    resumeExpDate={resumeExpDate}
                    onClose={() => setEnhancedProfileModalOpen(false)}
                    onLater={onUpdateProfileLater}
                    onDoItNow={() => {
                        setEnhancedProfileModalOpen(false);
                        history.push('hygienistProfile');
                    }}
                />
            </Popup>
            <Popup opened={isWorkExperienceModalOpen} onClose={() => setWorkExperienceModalOpen(false)}>
                <WorkExperienceModal
                    onClose={() => setWorkExperienceModalOpen(false)}
                />
            </Popup>
            {availableJobs.length ? availableJobs.map((job: JobProps) => {
                const {location, shifts, postedOn} = job;
                const listRateBenchmarkKey = rateBenchmarkKeyByJobId[job.id];
                const listRateBenchmark = listRateBenchmarkKey
                    ? listRateBenchmarks[listRateBenchmarkKey] ?? null
                    : null;
                const isGuaranteedRate = !!job?.ghr
                const shift = shifts[0];
                const isCancelledRepost = Boolean(job?.reposted || job?.shiftCancelled);
                const days = getDaysFromDate(postedOn)
                let postedSuffix = `on ${formatDate(postedOn, 'dddd MMMM D')}`
                if (days < 1) {
                    postedSuffix = 'Today'
                }
                if (days >= 1 && days <= 7) {
                    postedSuffix = `${days}d ago`
                }

                return (
                    <Shift
                        key={job.id}
                        className={clsx(job.isInRotation ? 'boosted' : undefined, { 'recently-viewed': highlightedShiftId === job.id })}
                        data-shift-id={job.id}
                    >
                        <div onClick={() => openModal(job.id, { date: shift.shiftDate })}>
                            <ShiftHeader className={'row'}>
                                <div className={'col'}>
                                    <div className={'row'}>
                                        <div>{location?.city}, {location?.province}</div>
                                        {job.isInRotation ? (
                                            <BoostBadge>
                                                <Icon
                                                    style={{ width: 18, height: 18, marginRight: 6 }}
                                                    src={spotlight}
                                                />
                                                Featured Shift
                                            </BoostBadge>
                                        ) : null}
                                        <div className='ctaTopRight'>
                                            {job.isFavouriteDentist ? (
                                                <button
                                                    className={clsx('quickApplyBtn no-fastclick', job.isInRotation ? 'desktopOnly' : undefined)}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const now = Date.now();
                                                        if (now - (lastQuickApplyClickTsRef.current || 0) < 500) return; // debounce double taps
                                                        lastQuickApplyClickTsRef.current = now;
                                                        const insertId = generateInsertId();
                                                        const payload = {
                                                            distinct_id: (window as any)?.TempStars?.User?.getCurrentUser?.()?.id,
                                                            job_id: job.id,
                                                            office_id: job.location?.id || job.locationId,
                                                            view_source: 'available_shifts_list',
                                                            platform: platformAndVersion.platform,
                                                            client_version: platformAndVersion.clientVersion,
                                                            eligibility_reason: 'loved_office',
                                                            is_loved_office: !!job.isFavouriteDentist,
                                                            shift_date: job?.shifts?.[0]?.shiftDate || job?.startDate,
                                                            role: 'hygienist',
                                                            pay_rate: job?.ghr || job?.proposedRate || job?.avgRate || job?.hourlyRate,
                                                            membership_status_code: membershipStatus?.currentStatusCode ?? null,
                                                            membership_rate_tier: rateTalent.status,
                                                            insert_id: insertId,
                                                        };
                                                        Analytics.track('quick_apply_click' as any, payload);
                                                        handleOpenQuickApply(job.id);
                                                    }}
                                                    disabled={updating}
                                                    aria-label='Quick Apply'
                                                >
                                                    QUICK APPLY
                                                </button>
                                            ) : null}
                                            <Icon
                                                style={{ width: 22, cursor: 'default' }}
                                                src={job.isFavouriteDentist ? heartFilled : heartUnfilled}
                                                alt={job.isFavouriteDentist ? 'Favourite office' : 'Not a favourite office'}
                                            />
                                        </div>
                                        {isCancelledRepost ? (
                                            <PriorityShiftContainer>
                                                <CancellationLabel
                                                    startDate={shift?.shiftDate}
                                                    postedStartTime={shift?.postedStart}
                                                    serverLabel={job?.cancellationLabel ?? null}
                                                />
                                            </PriorityShiftContainer>
                                        ) : null}
                                    </div>
                                    <div className='rateRow'>
                                        {job.type === 2 && <span style={{ fontSize: '0.9em', color: 'var(--text-secondary)' }}>Reception/Admin • </span>}
                                        {isGuaranteedRate ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                <span>{getDisplayRateRange(rateTalent, job.proposedRate || job.avgRate || job.hourlyRate, listRateBenchmark)}</span>
                                                <BonusRateTooltip job={job} />
                                            </div>
                                        ) : (
                                            getDisplayRateRange(rateTalent, job.proposedRate || job.avgRate || job.hourlyRate, listRateBenchmark)
                                        )}
                                    </div>
                                </div>
                            </ShiftHeader>
                            <div className='col'>
                                <div className='infoGrid'>
                                    <div>
                                        <DataBox title={"Hours"} data={`${shift?.postedStart} - ${shift?.postedEnd}`}/>
                                    </div>
                                    <div>
                                        <DataBox title={"Shift Date"} data={formatDate(shift.shiftDate, 'dddd MMMM D')}/>
                                    </div>
                                    {!job.type && (
                                        <div>
                                            <DataBox title={"Recall time"} data={formatAvgApptTime(location?.avgApptTime)} />
                                        </div>
                                    )}
                                    <div>
                                        <DataBox title={"Distance"} data={distance(job, hygienist?.country)}/>
                                    </div>
                                    {location?.software && (
                                        <div className='softwareSection'>
                                            <span className='softwareLabel'>Software</span>
                                            <span className='softwarePill'>{location.software}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className='cardFooter'>
                                <div className='row statuses'>
                                {(!!job.Instabook && canUseInstabookByMembership({
                                    membership: membershipStatus,
                                    isFavourite: isFavouriteHygienist(job),
                                })) && <div className='status'>
                                    <Icon className='instabookIcon' src={instabook}/> You can instantly book this shift.
                                </div>}
                                {/*{!!location.vaxRequired && <div className='status'>*/}
                                {/*    <Icon*/}
                                {/*        style={{ width: 18 }}*/}
                                {/*        src={vax}*/}
                                {/*    /> Requires Proof of Full Vaccination.*/}
                                {/*</div>}*/}
                                {!!location.friendlyEndorsed && <div className='status'>
                                    <Icon className='statusIcon' src={friendlyEndorsed}/> A friendly office.
                                </div>}
                                {!!location.equipEndorsed && <div className='status'>
                                    <Icon className='statusIcon' src={equipmentEndorsed}/> Great instruments.
                                </div>}
                                </div>
                                <div className='postedOn'>
                                    {`Shift posted: ${postedSuffix}`}
                                </div>
                            </div>
                            {/* Mobile-only CTA at bottom-right for boosted/featured */}
                            {Boolean(job.isInRotation) && (
                                <div className='ctaBottom mobileOnly'>
                                    {job.isFavouriteDentist ? (
                                        <button
                                            className='quickApplyBtn no-fastclick'
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const now = Date.now();
                                                if (now - (lastQuickApplyClickTsRef.current || 0) < 500) return;
                                                lastQuickApplyClickTsRef.current = now;
                                                const insertId = generateInsertId();
                                                const payload = {
                                                    distinct_id: (window as any)?.TempStars?.User?.getCurrentUser?.()?.id,
                                                    job_id: job.id,
                                                    office_id: job.location?.id || job.locationId,
                                                    view_source: 'available_shifts_list',
                                                    platform: platformAndVersion.platform,
                                                    client_version: platformAndVersion.clientVersion,
                                                    eligibility_reason: 'loved_office',
                                                    is_loved_office: !!job.isFavouriteDentist,
                                                    shift_date: job?.shifts?.[0]?.shiftDate || job?.startDate,
                                                    role: 'hygienist',
                                                    pay_rate: job?.ghr || job?.proposedRate || job?.avgRate || job?.hourlyRate,
                                                    membership_status_code: membershipStatus?.currentStatusCode ?? null,
                                                    membership_rate_tier: rateTalent.status,
                                                    insert_id: insertId,
                                                };
                                                Analytics.track('quick_apply_click' as any, payload);
                                                handleOpenQuickApply(job.id);
                                            }}
                                            disabled={updating}
                                            aria-label='Quick Apply'
                                        >
                                            QUICK APPLY
                                        </button>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </Shift>
                )
            }) : <div className={'noData'}>{updating || getResumesExpirationDateLoading ? '' : errorGetJos || emptyMessage || 'No shifts'}</div>}

            <MaxBookedPopup
                isOpen={isMaxBookedPopupOpen}
                onClose={() => {
                    setIsMaxBookedPopupOpen(false);
                }}
                canRequestIncrease={canRequestMaxBookedIncrease(membershipStatus, completedShifts)}
            />

            <MembershipRestrictionModal
                isOpen={!!membershipDialog}
                copy={membershipDialog}
                onClose={() => setMembershipDialog(null)}
            />

            <FixedLoader loading={updating || quickApplyLoading || getResumesExpirationDateLoading || getHygienistWorkExperienceLoading || checkFavouriteHygienistLocationsLoading || getHygienistOffersLoading || getHygienistJobsLoading}/>

            {/* Quick Apply Modal using existing OfferModal for rate/time selection */}
            {quickApplyOpenFor != null && (() => {
                const baseJob = availableJobs.find(j => j.id === quickApplyOpenFor);
                if (!baseJob) return null;
                const job = quickApplyOfferStats
                    ? { ...baseJob, offerStats: quickApplyOfferStats }
                    : baseJob;
                const partialAllowed = false; 
                const handleSubmit = async (data: { startTime: string; endTime: string; hourlyRate: number; isPartial: boolean; showJobSeekingStatus?: boolean; }) => {
                    const insertId = generateInsertId();
                    const payload = {
                        distinct_id: (window as any)?.TempStars?.User?.getCurrentUser?.()?.id,
                        job_id: job.id,
                        office_id: job.location?.id || job.locationId,
                        view_source: 'quick_apply_modal',
                        platform: platformAndVersion.platform,
                        client_version: platformAndVersion.clientVersion,
                        shift_date: job?.shifts?.[0]?.shiftDate || job?.startDate,
                        role: 'hygienist',
                        pay_rate: data?.hourlyRate,
                        membership_status_code: membershipStatus?.currentStatusCode ?? null,
                        membership_rate_tier: rateTalent.status,
                        cta_copy: 'Yes apply now',
                        insert_id: insertId,
                    };
                    Analytics.track('quick_apply_confirm_click' as any, payload);

                    await makeQuickApplyOffer({
                        hourlyRate: data.hourlyRate,
                        jobId: job.id,
                        startTime: data.startTime,
                        endTime: data.endTime,
                        isPartial: false,
                        showJobSeekingStatus: data?.showJobSeekingStatus ? 1 : 0,
                    });
                };
                return (
                    <OfferModal
                        open={true}
                        onClose={handleCloseQuickApply}
                        onSubmit={handleSubmit}
                        offerType={'full'}
                        job={job}
                        hygienist={hygienist}
                        membershipRateTier={rateTalent.status}
                        submitting={quickApplyLoading}
                        showJobSeekingStatus={showJobSeekingStatus}
                        setShowJobSeekingStatus={setShowJobSeekingStatus}
                        partialOfferAvailable={partialAllowed}
                        rateBenchmark={quickApplyRateBenchmark}
                        titleOverride={'Apply to this shift?'}
                        subtitleOverride={`You've previously worked at ${job.location.practiceName} and loved them. Would you like to apply for this shift now?`}
                        submitTextOverride={'Yes apply now'}
                        cancelTextOverride={"No, don't apply"}
                    />
                );
            })()}
        </ShiftsContainer>)
}

export default Shifts;
